import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  BadgeCheck, ShieldCheck, AlertCircle, TrendingUp, Sparkles, ArrowRight,
  Wrench, FolderLock, Share2, FileText,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SECTOR_LABEL = {
  insurance: 'Insurance broker',
  wealth: 'Wealth manager',
  solicitor: 'Solicitor',
  accountant: 'Accountant',
  other: 'Advisor',
};

const PillarCard = ({ icon: Icon, title, body }) => (
  <div className="luxury-card p-6 hover:border-gold/30 transition-colors">
    <div className="w-10 h-10 rounded bg-gold/10 border border-gold/30 flex items-center justify-center mb-4">
      <Icon size={18} className="text-gold" />
    </div>
    <h3 className="font-serif text-lg text-white mb-2">{title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
  </div>
);

export default function BrokerLanding() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true, error: null, firm: null });

  useEffect(() => {
    fetch(`${API}/api/brokers/by-slug/${slug}`)
      .then(async r => {
        if (!r.ok) {
          const body = await r.json().catch(() => ({}));
          setState({ loading: false, error: body.detail || `Error ${r.status}`, firm: null });
          return;
        }
        const data = await r.json();
        setState({ loading: false, error: null, firm: data });
      })
      .catch(() => setState({ loading: false, error: 'Could not load', firm: null }));
  }, [slug]);

  if (state.loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6 lg:p-12">
        <div className="max-w-5xl mx-auto">
          <Skeleton className="h-3 w-32 mb-3 bg-gold/10" />
          <Skeleton className="h-12 w-96 mb-6 bg-white/5" />
          <Skeleton className="h-5 w-2/3 bg-white/5" />
        </div>
      </div>
    );
  }

  if (state.error || !state.firm) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-6">
        <div className="luxury-card p-10 text-center max-w-md" data-testid="broker-landing-error">
          <AlertCircle size={32} className="text-amber-400 mx-auto mb-4" />
          <h1 className="font-serif text-2xl text-white mb-3">Firm not found</h1>
          <p className="text-sm text-zinc-400 mb-6">{state.error || 'This partner page is not available.'}</p>
          <Link to="/">
            <Button className="btn-primary">Back to Assagart</Button>
          </Link>
        </div>
      </div>
    );
  }

  const f = state.firm;
  const sectorLabel = SECTOR_LABEL[f.sector] || 'Trusted advisor';

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="broker-landing-page">
      {/* HERO with co-branding */}
      <section className="relative overflow-hidden border-b border-[#1A1A1A]">
        <div className="absolute inset-0 bg-gradient-to-br from-gold/[0.06] via-transparent to-emerald-500/[0.04]" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
          {/* Co-brand bar */}
          <div className="flex items-center justify-center gap-4 mb-10" data-testid="cobrand-bar">
            <img src="/assagart-logo.png?v=2" alt="Assagart" className="h-9 opacity-90" />
            <div className="text-zinc-600 text-2xl font-thin">×</div>
            <div className="text-white font-serif text-xl tracking-wide" data-testid="firm-name-hero">{f.firm_name}</div>
          </div>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 text-[11px] uppercase tracking-wider mb-6">
              <BadgeCheck size={13} className="text-emerald-400" />
              Verified {sectorLabel}
              {f.fca_number && <span className="text-emerald-300/80 normal-case tracking-normal">FCA #{f.fca_number}</span>}
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6 leading-tight">
              Track your wealth.<br />
              Understand your risks.<br />
              <span className="text-gold">Know what to do next.</span>
            </h1>
            <p className="text-base lg:text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              {f.firm_name} has partnered with Assagart to give their high-net-worth clients a single
              encrypted vault for every asset they own, a live Portfolio Health Score, and a one-click path
              to action when something needs attention.
            </p>
            <div className="flex items-center justify-center gap-3 mt-10 flex-wrap">
              <Link to={`/register?firm=${f.firm_slug}`}>
                <Button className="bg-gradient-to-r from-gold to-amber-300 text-black font-semibold px-6 py-6 hover:scale-105 transition-transform" data-testid="cta-create-account">
                  Create your private vault <ArrowRight size={14} className="ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button variant="outline" className="border-[#262626] text-white hover:border-white/30 px-6 py-6" data-testid="cta-existing-login">
                  I already have an Assagart account
                </Button>
              </Link>
            </div>
            <p className="text-xs text-muted-foreground mt-6">
              Your account is private to you. {f.firm_name} only sees what you explicitly share.
            </p>
          </div>
        </div>
      </section>

      {/* What Assagart does */}
      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
        <div className="text-center mb-12">
          <p className="text-gold uppercase tracking-[0.2em] text-xs mb-3">What Assagart does</p>
          <h2 className="font-serif text-3xl lg:text-4xl text-white">Three pillars, one platform</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <PillarCard icon={FolderLock} title="Track" body="One encrypted vault for cars, art, watches, jewellery, wine, handbags. Live valuations, insurance schedules, certificates, and receipts - all in one place." />
          <PillarCard icon={AlertCircle} title="Understand" body="A live Portfolio Health Score across coverage, diversification, and exposure. We surface what is underinsured, expiring, or too concentrated, before it costs you." />
          <PillarCard icon={Wrench} title="Act" body={`Every issue surfaces a "Fix this" button that drafts a pre-filled message straight to ${f.firm_name}. Your existing relationship becomes one click away.`} />
        </div>
      </section>

      {/* How it works with the firm */}
      <section className="bg-[#070707] border-y border-[#1A1A1A]">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-gold uppercase tracking-[0.2em] text-xs mb-3">{f.firm_name} × Assagart</p>
              <h2 className="font-serif text-3xl text-white mb-4">A safer way to share with your broker</h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                Sensitive information - your NI number, passport, tax ID, certificates of authenticity -
                is encrypted at rest with AES-256. When {f.firm_name} needs a field, you grant a one-time
                secure view that expires automatically. Nothing is ever stored on their side or sent
                over unencrypted email.
              </p>
              <ul className="space-y-3 text-sm text-zinc-300">
                <li className="flex items-start gap-3">
                  <Share2 size={14} className="text-emerald-400 flex-shrink-0 mt-1" />
                  One-time secure PII sharing - decrypted exactly once, then sealed forever
                </li>
                <li className="flex items-start gap-3">
                  <FileText size={14} className="text-emerald-400 flex-shrink-0 mt-1" />
                  Documents stored in encrypted object storage, never publicly listable
                </li>
                <li className="flex items-start gap-3">
                  <BadgeCheck size={14} className="text-emerald-400 flex-shrink-0 mt-1" />
                  {f.firm_name} carries a verified trust badge across the platform
                </li>
                <li className="flex items-start gap-3">
                  <Sparkles size={14} className="text-emerald-400 flex-shrink-0 mt-1" />
                  Renewal alerts, valuation refreshes, and concentration warnings - automatically
                </li>
              </ul>
            </div>
            <div className="luxury-card p-8 border-emerald-500/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
                  <BadgeCheck size={18} className="text-emerald-400" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-400">Verified partner</p>
                  <p className="font-serif text-lg text-white">{f.firm_name}</p>
                </div>
              </div>
              {f.fca_number && (
                <p className="text-xs text-muted-foreground mb-2">FCA Reference Number: <span className="text-white font-mono">{f.fca_number}</span></p>
              )}
              <p className="text-xs text-muted-foreground mb-4">Sector: <span className="text-white capitalize">{sectorLabel}</span></p>
              <p className="text-[11px] text-emerald-200/70 leading-relaxed border-t border-[#1A1A1A] pt-4 mt-4">
                When you correspond with anyone @{f.email_domain} through Assagart, you will see this
                verified trust badge automatically. We have manually verified their FCA registration and
                business credentials.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="max-w-6xl mx-auto px-6 lg:px-12 py-16 text-center">
        <p className="text-gold uppercase tracking-[0.2em] text-xs mb-3">Simple pricing</p>
        <h2 className="font-serif text-3xl text-white mb-2">Start free. Upgrade when you outgrow it.</h2>
        <p className="text-sm text-muted-foreground max-w-xl mx-auto mb-10">
          The Starter tier is free forever. Professional gives you everything to run your wealth well -
          AI valuations, Risk page, Wealth Dossier. Elite adds the Advisor Hub so your broker works inside Assagart with you.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto">
          {[
            { name: 'Starter', price: 'Free', body: 'Up to 5 assets. Basic vault. The on-ramp.' },
            { name: 'Professional', price: '£29/mo', body: 'Unlimited assets, AI valuations, Risk page, Health Score, Timeline, Contextual AI Insights, Wealth Dossier PDF.', highlight: true },
            { name: 'Elite', price: '£79/mo', body: 'Everything in Pro PLUS Advisor Hub, in-app reply thread, PII Secure Share, priority valuations, family seats, concierge.' },
          ].map(t => (
            <div key={t.name} className={`luxury-card p-6 text-left ${t.highlight ? 'border-gold/40' : ''}`}>
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">{t.name}</p>
              <p className="font-serif text-3xl text-white mb-3">{t.price}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{t.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonial */}
      {f.testimonial_quote && (
        <section className="border-t border-[#1A1A1A] bg-gradient-to-b from-transparent to-gold/[0.02]" data-testid="broker-testimonial">
          <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16">
            <p className="text-gold uppercase tracking-[0.2em] text-xs text-center mb-6">In their words</p>
            <blockquote className="font-serif text-2xl lg:text-3xl text-white leading-snug text-center">
              <span className="text-gold mr-2">&ldquo;</span>
              {f.testimonial_quote}
              <span className="text-gold ml-2">&rdquo;</span>
            </blockquote>
            {(f.testimonial_author || f.testimonial_role) && (
              <p className="text-sm text-muted-foreground text-center mt-6">
                {f.testimonial_author && <span className="text-white">{f.testimonial_author}</span>}
                {f.testimonial_author && f.testimonial_role && <span className="mx-2 text-zinc-600">·</span>}
                {f.testimonial_role}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section className="border-t border-[#1A1A1A] bg-gradient-to-b from-transparent to-gold/[0.03]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16 text-center">
          <h2 className="font-serif text-3xl text-white mb-4">Take five minutes today.</h2>
          <p className="text-sm text-muted-foreground mb-8">
            Your private vault opens immediately. {f.firm_name} only sees what you choose to share.
          </p>
          <Link to={`/register?firm=${f.firm_slug}`}>
            <Button className="bg-gradient-to-r from-gold to-amber-300 text-black font-semibold px-8 py-6" data-testid="cta-bottom">
              Create your private vault <ArrowRight size={14} className="ml-2" />
            </Button>
          </Link>
          <p className="text-[10px] text-muted-foreground mt-8">
            Assagart Ltd. · <Link to="/privacy" className="hover:text-gold">Privacy</Link> · <Link to="/terms" className="hover:text-gold">Terms</Link>
          </p>
          <p className="text-[11px] text-muted-foreground mt-4">
            Are you an FCA-registered advisor looking to join the verified network?{' '}
            <Link to={`/brokers/${f.firm_slug}/join`} className="text-gold hover:underline" data-testid="broker-apply-link">
              Apply to partner with Assagart
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
}
